import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  Hotel,
  Refresh,
  Home,
  Search,
  AccessTime,
  DoNotDisturbOn,
  CleaningServices,
  Build,
  LocalLaundryService,
  Spa,
  RoomService,
  HelpOutline,
  Event,
  Bed,
  SupportAgent,
  CheckCircle,
  PlayArrow,
  Visibility,
  MeetingRoom,
  Bolt,
  Close,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  DUMMY_ROOMS,
  DUMMY_REQUESTS,
  REQUEST_TYPE_META,
  STATUS_META,
} from "./dummyData";

/* Modern ops palette — deep ink + seafoam (not purple / cream kitsch) */
const C = {
  bg: "#0b1220",
  bgSoft: "#111827",
  panel: "rgba(17, 24, 39, 0.72)",
  card: "rgba(255, 255, 255, 0.045)",
  cardHover: "rgba(255, 255, 255, 0.075)",
  glass: "rgba(255, 255, 255, 0.04)",
  border: "rgba(255, 255, 255, 0.08)",
  borderStrong: "rgba(255, 255, 255, 0.14)",
  text: "#f3f6fb",
  muted: "#94a3b8",
  faint: "#64748b",
  accent: "#2dd4bf",
  accentDeep: "#14b8a6",
  danger: "#fb7185",
  warn: "#fbbf24",
  info: "#38bdf8",
  ok: "#34d399",
};

const FONT = "'Poppins', system-ui, sans-serif";

const TYPE_ICONS = {
  cleaning: CleaningServices,
  laundry: LocalLaundryService,
  build: Build,
  spa: Spa,
  concierge: SupportAgent,
  help: HelpOutline,
  dnd: DoNotDisturbOn,
  bed: Bed,
  food: RoomService,
  event: Event,
};

const formatAge = (iso) => {
  if (!iso) return "—";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
};

const TypeIcon = ({ type, size = 18 }) => {
  const meta = REQUEST_TYPE_META[type] || REQUEST_TYPE_META.help;
  const Icon = TYPE_ICONS[meta.icon] || HelpOutline;
  return <Icon sx={{ fontSize: size, color: meta.color }} />;
};

const GuestHubAdmin = () => {
  const navigate = useNavigate();
  const [rooms] = useState(DUMMY_ROOMS);
  const [requests, setRequests] = useState(DUMMY_REQUESTS);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const openStatuses = useMemo(
    () => new Set(["requested", "acknowledged", "in_progress"]),
    []
  );

  const requestCountsByRoom = useMemo(() => {
    const map = {};
    requests.forEach((r) => {
      if (!openStatuses.has(r.status)) return;
      map[r.tableId] = (map[r.tableId] || 0) + 1;
    });
    return map;
  }, [requests, openStatuses]);

  const stats = useMemo(() => {
    const open = requests.filter((r) => openStatuses.has(r.status)).length;
    const neu = requests.filter((r) => r.status === "requested").length;
    const dnd = rooms.filter((r) => r.doNotDisturb).length;
    const makeup = rooms.filter((r) => r.makeupRequested).length;
    const occupied = rooms.filter((r) => r.guestName).length;
    return { open, neu, dnd, makeup, occupied, rooms: rooms.length };
  }, [requests, rooms, openStatuses]);

  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests
      .filter((r) => {
        if (selectedRoomId && r.tableId !== selectedRoomId) return false;
        if (typeFilter !== "all" && r.type !== typeFilter) return false;
        if (statusFilter === "open" && !openStatuses.has(r.status)) return false;
        if (statusFilter !== "open" && statusFilter !== "all" && r.status !== statusFilter)
          return false;
        if (!q) return true;
        return (
          r.title?.toLowerCase().includes(q) ||
          r.locationLabel?.toLowerCase().includes(q) ||
          r.details?.toLowerCase().includes(q) ||
          r.type?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [requests, selectedRoomId, typeFilter, statusFilter, search, openStatuses]);

  const advanceStatus = (id) => {
    setRequests((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = STATUS_META[r.status]?.next;
        if (!next) return r;
        return { ...r, status: next, updatedAt: new Date().toISOString() };
      })
    );
  };

  const typeFilters = [
    { key: "all", label: "All" },
    ...Object.entries(REQUEST_TYPE_META)
      .filter(([k]) => !["food", "events"].includes(k))
      .map(([key, meta]) => ({ key, label: meta.label, color: meta.color })),
  ];

  const statusTabs = [
    { key: "open", label: "Open", count: stats.open },
    { key: "requested", label: "New", count: stats.neu },
    { key: "in_progress", label: "Active" },
    { key: "completed", label: "Done" },
    { key: "all", label: "All" },
  ];

  const timeLabel = clock.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        color: C.text,
        fontFamily: FONT,
        overflow: "hidden",
        position: "relative",
        background: `
          radial-gradient(1200px 600px at 8% -10%, rgba(45, 212, 191, 0.16), transparent 55%),
          radial-gradient(900px 500px at 92% 0%, rgba(56, 189, 248, 0.1), transparent 50%),
          radial-gradient(700px 400px at 50% 110%, rgba(251, 113, 133, 0.08), transparent 45%),
          linear-gradient(165deg, #070b14 0%, #0b1220 45%, #0f172a 100%)
        `,
      }}
    >
      {/* Ambient grid */}
      <Box
        aria-hidden
        sx={{
          pointerEvents: "none",
          position: "absolute",
          inset: 0,
          opacity: 0.35,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at center, black 20%, transparent 75%)",
        }}
      />

      {/* Header */}
      <Box
        component={motion.header}
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        sx={{
          position: "relative",
          zIndex: 2,
          mx: { xs: 1.5, md: 2 },
          mt: { xs: 1.5, md: 2 },
          px: { xs: 2, md: 2.5 },
          py: 1.75,
          borderRadius: 3.5,
          bgcolor: C.panel,
          border: `1px solid ${C.border}`,
          backdropFilter: "blur(18px)",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2.5,
              background: `linear-gradient(145deg, ${alpha(C.accent, 0.35)}, ${alpha(C.info, 0.15)})`,
              border: `1px solid ${alpha(C.accent, 0.35)}`,
              color: C.accent,
              display: "grid",
              placeItems: "center",
              boxShadow: `0 0 24px ${alpha(C.accent, 0.25)}`,
            }}
          >
            <Hotel />
          </Box>
          <Box minWidth={0}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.2rem",
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
              }}
            >
              Guest Hub
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.35 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: C.ok,
                  boxShadow: `0 0 10px ${C.ok}`,
                  animation: "pulseDot 2s ease-in-out infinite",
                  "@keyframes pulseDot": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.45 },
                  },
                }}
              />
              <Typography sx={{ color: C.muted, fontSize: "0.75rem", fontWeight: 500 }}>
                Live board · Demo · {timeLabel}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: 1,
            flex: 1,
            minWidth: { xs: "100%", md: 360 },
          }}
        >
          <StatCard label="Open" value={stats.open} tone={C.danger} />
          <StatCard label="New" value={stats.neu} tone={C.warn} />
          <StatCard label="DND" value={stats.dnd} tone="#f87171" />
          <StatCard label="Make-up" value={stats.makeup} tone={C.warn} />
          <StatCard label="In-house" value={`${stats.occupied}/${stats.rooms}`} tone={C.accent} />
        </Box>

        <Box sx={{ display: "flex", gap: 0.75, ml: { md: "auto" } }}>
          <GlassIconButton title="Reset demo data" onClick={() => setRequests([...DUMMY_REQUESTS])}>
            <Refresh fontSize="small" />
          </GlassIconButton>
          <GlassIconButton title="Dashboard" onClick={() => navigate("/dashboard")}>
            <Home fontSize="small" />
          </GlassIconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.08fr) minmax(340px, 0.92fr)" },
          gap: { xs: 1.5, md: 2 },
          p: { xs: 1.5, md: 2 },
          pt: { xs: 1.5, md: 2 },
        }}
      >
        {/* Rooms */}
        <Box
          component={motion.section}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          sx={{
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderRadius: 4,
            bgcolor: C.panel,
            border: `1px solid ${C.border}`,
            backdropFilter: "blur(16px)",
            overflow: "hidden",
            boxShadow: "0 16px 48px rgba(0,0,0,0.28)",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <MeetingRoom sx={{ color: C.accent, fontSize: 20 }} />
              <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", letterSpacing: "-0.02em" }}>
                Rooms
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 99,
                  bgcolor: alpha(C.accent, 0.12),
                  color: C.accent,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                }}
              >
                {rooms.length}
              </Box>
            </Box>
            <AnimatePresence>
              {selectedRoomId && (
                <Button
                  component={motion.button}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  size="small"
                  startIcon={<Close sx={{ fontSize: "14px !important" }} />}
                  onClick={() => setSelectedRoomId(null)}
                  sx={{
                    textTransform: "none",
                    color: C.muted,
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    borderRadius: 99,
                    px: 1.25,
                    bgcolor: alpha("#fff", 0.04),
                    border: `1px solid ${C.border}`,
                    "&:hover": { bgcolor: alpha("#fff", 0.08), color: C.text },
                  }}
                >
                  Clear filter
                </Button>
              )}
            </AnimatePresence>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 2,
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
              alignContent: "start",
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha("#fff", 0.12),
                borderRadius: 99,
              },
            }}
          >
            {rooms.map((room, index) => {
              const openCount = requestCountsByRoom[room.id] || 0;
              const selected = selectedRoomId === room.id;
              const vacant = !room.guestName;
              const urgent = openCount > 0;

              return (
                <Box
                  key={room.id}
                  component={motion.button}
                  type="button"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.03 * index }}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedRoomId(selected ? null : room.id)}
                  sx={{
                    all: "unset",
                    cursor: "pointer",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    minHeight: 132,
                    p: 1.75,
                    borderRadius: 3,
                    bgcolor: selected ? alpha(C.accent, 0.1) : C.card,
                    border: `1px solid ${selected ? alpha(C.accent, 0.55) : C.border}`,
                    boxShadow: selected
                      ? `0 0 0 1px ${alpha(C.accent, 0.25)}, 0 12px 32px ${alpha(C.accent, 0.12)}`
                      : "0 8px 24px rgba(0,0,0,0.18)",
                    overflow: "hidden",
                    transition: "background 0.2s, border-color 0.2s",
                    "&:hover": { bgcolor: selected ? alpha(C.accent, 0.12) : C.cardHover },
                    "&::before": urgent
                      ? {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 2,
                          background: `linear-gradient(90deg, ${C.danger}, ${C.warn})`,
                        }
                      : {},
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "1.65rem",
                        letterSpacing: "-0.05em",
                        lineHeight: 1,
                        background: selected
                          ? `linear-gradient(135deg, ${C.text}, ${C.accent})`
                          : "none",
                        WebkitBackgroundClip: selected ? "text" : "unset",
                        WebkitTextFillColor: selected ? "transparent" : C.text,
                      }}
                    >
                      {room.tableNumber}
                    </Typography>

                    {urgent ? (
                      <Box
                        sx={{
                          minWidth: 26,
                          height: 26,
                          px: 0.75,
                          borderRadius: 99,
                          bgcolor: alpha(C.danger, 0.18),
                          border: `1px solid ${alpha(C.danger, 0.4)}`,
                          color: C.danger,
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 800,
                          fontSize: "0.75rem",
                        }}
                      >
                        {openCount}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          bgcolor: vacant ? C.faint : C.ok,
                          boxShadow: vacant ? "none" : `0 0 10px ${alpha(C.ok, 0.7)}`,
                          mt: 0.75,
                        }}
                      />
                    )}
                  </Box>

                  <Typography sx={{ color: C.faint, fontSize: "0.7rem", fontWeight: 500, lineHeight: 1.3 }}>
                    {room.location}
                  </Typography>

                  <Typography
                    sx={{
                      mt: "auto",
                      fontSize: "0.84rem",
                      fontWeight: 600,
                      color: vacant ? C.faint : C.text,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {room.guestName || "Vacant"}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", minHeight: 22, alignItems: "center" }}>
                    {room.doNotDisturb && <MiniTag tone={C.danger} icon={DoNotDisturbOn} label="DND" />}
                    {room.makeupRequested && <MiniTag tone={C.warn} icon={CleaningServices} label="Make-up" />}
                    {room.checkOut && !vacant && !room.doNotDisturb && !room.makeupRequested && (
                      <Typography sx={{ color: C.faint, fontSize: "0.68rem", fontWeight: 500 }}>
                        Out {room.checkOut}
                      </Typography>
                    )}
                    {vacant && (
                      <Typography sx={{ color: C.faint, fontSize: "0.68rem", fontWeight: 500 }}>
                        Ready
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Requests */}
        <Box
          component={motion.section}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.12 }}
          sx={{
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderRadius: 4,
            bgcolor: C.panel,
            border: `1px solid ${C.border}`,
            backdropFilter: "blur(16px)",
            overflow: "hidden",
            boxShadow: "0 16px 48px rgba(0,0,0,0.28)",
          }}
        >
          <Box sx={{ px: 2.5, pt: 1.75, pb: 1.5, borderBottom: `1px solid ${C.border}` }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Bolt sx={{ color: C.warn, fontSize: 20 }} />
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", letterSpacing: "-0.02em" }}>
                  Requests
                </Typography>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 99,
                    bgcolor: alpha(C.info, 0.12),
                    color: C.info,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  {filteredRequests.length}
                </Box>
              </Box>
              {selectedRoomId && (
                <Typography sx={{ color: C.accent, fontSize: "0.78rem", fontWeight: 600 }}>
                  Room {rooms.find((r) => r.id === selectedRoomId)?.tableNumber}
                </Typography>
              )}
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search room, guest need, details…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: C.faint }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2.5,
                  bgcolor: alpha("#fff", 0.035),
                  color: C.text,
                  fontFamily: FONT,
                  fontSize: "0.88rem",
                  "& fieldset": { borderColor: C.border },
                  "&:hover fieldset": { borderColor: C.borderStrong },
                  "&.Mui-focused fieldset": { borderColor: alpha(C.accent, 0.55) },
                },
                "& .MuiInputBase-input::placeholder": { color: C.faint, opacity: 1 },
              }}
            />

            {/* Segmented status */}
            <Box
              sx={{
                display: "flex",
                p: 0.4,
                mb: 1.25,
                borderRadius: 2.5,
                bgcolor: alpha("#000", 0.28),
                border: `1px solid ${C.border}`,
                gap: 0.35,
                overflowX: "auto",
              }}
            >
              {statusTabs.map((s) => {
                const active = statusFilter === s.key;
                return (
                  <Box
                    key={s.key}
                    component="button"
                    type="button"
                    onClick={() => setStatusFilter(s.key)}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      flex: 1,
                      minWidth: "fit-content",
                      textAlign: "center",
                      px: 1.25,
                      py: 0.7,
                      borderRadius: 2,
                      fontSize: "0.74rem",
                      fontWeight: 600,
                      letterSpacing: "-0.01em",
                      color: active ? C.bg : C.muted,
                      bgcolor: active ? C.accent : "transparent",
                      boxShadow: active ? `0 4px 16px ${alpha(C.accent, 0.35)}` : "none",
                      transition: "all 0.18s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.label}
                    {s.count != null ? ` · ${s.count}` : ""}
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 0.65,
                overflowX: "auto",
                pb: 0.25,
                "&::-webkit-scrollbar": { height: 0 },
              }}
            >
              {typeFilters.map((t) => {
                const active = typeFilter === t.key;
                const color = t.color || C.accent;
                return (
                  <Box
                    key={t.key}
                    component="button"
                    type="button"
                    onClick={() => setTypeFilter(t.key)}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      flexShrink: 0,
                      px: 1.15,
                      py: 0.55,
                      borderRadius: 99,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: active ? color : C.muted,
                      bgcolor: active ? alpha(color, 0.14) : alpha("#fff", 0.03),
                      border: `1px solid ${active ? alpha(color, 0.45) : C.border}`,
                      transition: "all 0.15s ease",
                      "&:hover": { borderColor: alpha(color, 0.4), color },
                    }}
                  >
                    {t.label}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha("#fff", 0.12),
                borderRadius: 99,
              },
            }}
          >
            {filteredRequests.length === 0 && (
              <Box
                sx={{
                  py: 8,
                  textAlign: "center",
                  borderRadius: 3,
                  border: `1px dashed ${C.borderStrong}`,
                  bgcolor: alpha("#fff", 0.02),
                }}
              >
                <Typography sx={{ fontWeight: 600, mb: 0.5, color: C.text }}>All clear</Typography>
                <Typography sx={{ fontSize: "0.82rem", color: C.muted }}>
                  No requests match these filters.
                </Typography>
              </Box>
            )}

            {filteredRequests.map((req, index) => {
              const typeMeta = REQUEST_TYPE_META[req.type] || REQUEST_TYPE_META.help;
              const statusMeta = STATUS_META[req.status] || STATUS_META.requested;
              const isNew = req.status === "requested";
              const nextLabel =
                statusMeta.next === "acknowledged"
                  ? "Acknowledge"
                  : statusMeta.next === "in_progress"
                    ? "Start"
                    : statusMeta.next === "completed"
                      ? "Complete"
                      : null;
              const NextIcon =
                statusMeta.next === "acknowledged"
                  ? Visibility
                  : statusMeta.next === "in_progress"
                    ? PlayArrow
                    : CheckCircle;

              return (
                <Box
                  key={req.id}
                  component={motion.article}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22, delay: Math.min(index * 0.03, 0.18) }}
                  sx={{
                    position: "relative",
                    flexShrink: 0,
                    width: "100%",
                    boxSizing: "border-box",
                    p: 1.75,
                    pl: 2.25,
                    borderRadius: 3,
                    bgcolor: C.card,
                    border: `1px solid ${C.border}`,
                    borderLeft: `3px solid ${typeMeta.color}`,
                    boxShadow: isNew ? `inset 3px 0 12px ${alpha(typeMeta.color, 0.15)}` : "none",
                    transition: "border-color 0.2s, background 0.2s",
                    "&:hover": {
                      bgcolor: C.cardHover,
                      borderColor: alpha(typeMeta.color, 0.35),
                      borderLeftColor: typeMeta.color,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2.25,
                        flexShrink: 0,
                        bgcolor: alpha(typeMeta.color, 0.14),
                        border: `1px solid ${alpha(typeMeta.color, 0.25)}`,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <TypeIcon type={req.type} size={19} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
                        <Typography
                          sx={{
                            flex: 1,
                            fontWeight: 700,
                            fontSize: "0.92rem",
                            letterSpacing: "-0.02em",
                            lineHeight: 1.35,
                          }}
                        >
                          {req.title}
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.4,
                            color: C.faint,
                            flexShrink: 0,
                            mt: 0.15,
                          }}
                        >
                          <AccessTime sx={{ fontSize: 13 }} />
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, lineHeight: 1.2 }}>
                            {formatAge(req.createdAt)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexWrap: "wrap",
                          mb: req.details ? 0.85 : 1.1,
                        }}
                      >
                        <Typography
                          sx={{
                            color: C.muted,
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            lineHeight: 1.4,
                          }}
                        >
                          {req.locationLabel} · {typeMeta.label}
                          {req.orderNo ? ` · ${req.orderNo}` : ""}
                        </Typography>
                        <Box
                          sx={{
                            px: 0.85,
                            py: 0.25,
                            borderRadius: 99,
                            bgcolor: alpha(statusMeta.color, 0.15),
                            color: statusMeta.color,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            letterSpacing: "0.02em",
                            textTransform: "uppercase",
                            lineHeight: 1.3,
                          }}
                        >
                          {statusMeta.label}
                        </Box>
                      </Box>

                      {req.details && (
                        <Typography
                          sx={{
                            fontSize: "0.82rem",
                            color: alpha(C.text, 0.82),
                            lineHeight: 1.45,
                            mb: 1.1,
                          }}
                        >
                          {req.details}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.7rem", color: C.faint, fontWeight: 500, lineHeight: 1.3 }}>
                          {req.etaMinutes != null ? `ETA ~${req.etaMinutes} min` : "No ETA"}
                          {req.active ? " · Active" : ""}
                        </Typography>

                        {nextLabel && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<NextIcon sx={{ fontSize: "15px !important" }} />}
                            onClick={() => advanceStatus(req.id)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              fontSize: "0.78rem",
                              letterSpacing: "-0.01em",
                              borderRadius: 2.25,
                              px: 1.5,
                              py: 0.55,
                              minHeight: 0,
                              bgcolor: alpha(C.accent, 0.9),
                              color: "#042f2e",
                              boxShadow: `0 6px 18px ${alpha(C.accent, 0.28)}`,
                              "&:hover": {
                                bgcolor: C.accent,
                                boxShadow: `0 8px 22px ${alpha(C.accent, 0.4)}`,
                              },
                            }}
                          >
                            {nextLabel}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const StatCard = ({ label, value, tone }) => (
  <Box
    sx={{
      px: 1.25,
      py: 1,
      borderRadius: 2.25,
      bgcolor: alpha("#fff", 0.03),
      border: `1px solid ${C.border}`,
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: { xs: "1rem", md: "1.15rem" },
        color: tone,
        lineHeight: 1,
        letterSpacing: "-0.03em",
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ mt: 0.45, fontSize: "0.65rem", color: C.faint, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
      {label}
    </Typography>
  </Box>
);

const MiniTag = ({ tone, icon: Icon, label }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.35,
      px: 0.7,
      py: 0.2,
      borderRadius: 99,
      bgcolor: alpha(tone, 0.12),
      border: `1px solid ${alpha(tone, 0.28)}`,
      color: tone,
      fontSize: "0.62rem",
      fontWeight: 700,
      letterSpacing: "0.02em",
    }}
  >
    <Icon sx={{ fontSize: 11 }} />
    {label}
  </Box>
);

const GlassIconButton = ({ title, onClick, children }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        color: C.muted,
        bgcolor: alpha("#fff", 0.04),
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        width: 36,
        height: 36,
        "&:hover": { bgcolor: alpha("#fff", 0.09), color: C.text },
      }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

export default GuestHubAdmin;
